// kit v2 — THE REBUILT CONSOLE'S PIECES (UI rebuild batch 1; docs/DESIGN-SYSTEM.md v2 §8). Used only by the v2 shell
// and the rebuilt screens (lib/ui-v2.ts). The old kit (components/kit.tsx) stays what every current screen draws; the
// state pieces — StateLine, ReadFailed, ServerLine, EmptyState, Pending, Money, Address — are shared, re-exported here
// so a rebuilt screen imports from one place.
//
//   PageHead      a screen's title, one muted line, its actions at the right            text-page
//   Panel         a card with a head bar (title, a count, one link) and its body          text-title
//   Tabs          the views of one screen, words on a hairline, the lit one in the accent text-body
//   Notice        one boxed line that leads the page: a failed read, a held state, a warning
//   TaskRow       one thing that needs him: icon, what, who/how much, the one button
//   Tile          a door to another screen: icon, name, ≤ 4 words of what it is for
//   SummaryStrip  two to four figures that answer the screen's question                  text-figure
//   SkeletonRows  a first read on its way — says "reading", never "empty"
//   Register      a list's rows: a table from lg, cards on the phone, from one row spec          (batch 2)
//   SearchBar     search first; the rarer filters behind one Filters button
//   DetailSheet   one record's focused view over its list (right panel on the laptop, full screen on the phone)
//   Fact          a label and its value on one line, in a detail view
//
// THE RULES THEY CARRY: one gold primary per screen (a TaskRow's button is secondary); a figure is mono and tabular;
// words stay inside the budget (a label ≤ 3 words, a line ≤ 10, a tile's second line ≤ 4); colour underlines a word,
// never replaces it.
import { useEffect, useId, useMemo, useRef, useState, type KeyboardEvent as ReactKeyboardEvent, type ReactNode } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowDown, ArrowUp, ChevronRight, Search, SlidersHorizontal, X, type LucideIcon } from 'lucide-react';
import { OfflineLine, ReadFailed } from '../kit';
import { offlineFailure } from '../../lib/read-state';
import { fmtStamp } from '../../lib/format';

export { StateLine, ReadFailed, OfflineLine, ServerLine, EmptyState, Money, Address, Pending, Label, TAP } from '../kit';

/** A screen's head: the title (its routes.ts name), one muted line, and the screen's actions at the right — the
 *  primary last. On the phone the actions take the row beneath. */
export function PageHead({ title, sub, marks, actions, inline = false, children }: {
  title: ReactNode;
  sub?: ReactNode;
  /** Labels that belong to the title (a customer's side and methods): under the muted line, above the actions on the phone. */
  marks?: ReactNode;
  actions?: ReactNode;
  /** Keep the actions beside the title on the phone too (Astra's Trades / Parties head: one short primary). */
  inline?: boolean;
  children?: ReactNode;
}) {
  return (
    <header className="mb-4 space-y-3 lg:mb-6">
      <div className={`flex items-start justify-between gap-x-6 gap-y-3 ${inline ? '' : 'flex-wrap'}`}>
        <div className="min-w-0">
          <h1 className="text-page font-semibold">{title}</h1>
          {sub && <p className="mt-1 text-meta text-tg-hint">{sub}</p>}
          {marks && <div className="mt-2">{marks}</div>}
        </div>
        {/* The page's actions sit at the RIGHT on every width (Astra's audit P11, 1 Nov 2026: on the phone the gold primary dropped
            to a row at the left under the title): beside the title when `inline`, else on their own row, right-aligned. */}
        {actions && <div className={`flex flex-wrap items-center justify-end gap-2 lg:w-auto ${inline ? 'shrink-0' : 'w-full'}`}>{actions}</div>}
      </div>
      {children}
    </header>
  );
}

/** The panel: a head bar (title, an aside such as a count, one link at the right) over its body. `flush` drops the
 *  body's padding for a table or a row list that draws its own. */
export function Panel({ title, aside, action, flush = false, children, className = '' }: {
  title?: ReactNode;
  aside?: ReactNode;
  action?: ReactNode;
  flush?: boolean;
  children: ReactNode;
  className?: string;
}) {
  return (
    <section className={`overflow-hidden rounded-[10px] border border-stroke bg-panel supports-[overflow:clip]:overflow-clip ${className}`}>
      {(title || action) && (
        <div className="flex min-h-[52px] items-center justify-between gap-4 border-b border-stroke px-4 py-3 lg:px-5">
          <h2 className="min-w-0 text-title font-semibold">
            {title}
            {aside && <span className="ml-2 text-meta font-normal text-tg-hint">{aside}</span>}
          </h2>
          {action && <div className="shrink-0 text-body">{action}</div>}
        </div>
      )}
      <div className={flush ? '' : 'p-4 lg:p-5'}>{children}</div>
    </section>
  );
}

/** The views of one screen. Each tab is a link (the view is in the URL, so Back and a shared link land on it). */
export function Tabs({ items, current, label, wrap = false }: {
  /** `short`: the tab's word below lg, so a row of tabs fits one line on the phone (DESIGN-SYSTEM §10.9). */
  items: ReadonlyArray<{ id: string; label: string; short?: string; to: string; count?: number | null }>;
  current: string;
  label: string;
  /** Wrap onto a second row on a narrow phone instead of scrolling sideways (a scrolled-away tab is a hidden tab). */
  wrap?: boolean;
}) {
  return (
    <nav aria-label={label} className={`mb-4 flex gap-x-6 border-b border-stroke lg:mb-5 ${wrap ? 'flex-wrap' : 'overflow-x-auto'}`}>
      {items.map((t) => (
        <Link key={t.id} to={t.to} aria-current={t.id === current ? 'page' : undefined} className={`tab ${t.id === current ? 'tab-on' : ''}`}>
          {t.short ? <><span className="lg:hidden">{t.short}</span><span className="max-lg:hidden">{t.label}</span></> : t.label}
          {t.count != null && <span className="font-mono tabular text-meta text-tg-hint">{t.count}</span>}
        </Link>
      ))}
    </nav>
  );
}

const NOTICE_TONE = {
  warn: 'border-warn/25 bg-warn-bg text-warn',
  bad: 'border-neg/25 bg-neg-bg text-neg',
  neutral: 'border-stroke bg-panel-2 text-tg-hint',
} as const;

/** ONE BOXED LINE AT THE TOP OF A PAGE: what happened and the way out (a failed read and Try again; a paused read; a
 *  held state). Red fails or refuses, amber needs him, grey is plain. ≤ 10 words; detail goes behind the action. */
export function Notice({ tone = 'warn', children, action }: { tone?: keyof typeof NOTICE_TONE; children: ReactNode; action?: ReactNode }) {
  return (
    <div role={tone === 'bad' ? 'alert' : undefined} className={`mb-4 flex flex-wrap items-center justify-between gap-3 rounded-lg border px-4 py-3 text-body lg:mb-5 ${NOTICE_TONE[tone]}`}>
      <span className="min-w-0">{children}</span>
      {action && <span className="shrink-0">{action}</span>}
    </div>
  );
}

/** A FAILED READ THAT TAKES THE PAGE: the red box with ReadFailed and its Try again — or, when the read failed only for want
 *  of a connection (lib/read-state.ts offlineFailure), a grey box that says it is waiting (the shell reads again when the
 *  connection is back). */
export function ReadNotice({ what, error, onRetry }: { what: string; error: unknown; onRetry?: () => void }) {
  return (
    <Notice tone={offlineFailure(error) ? 'neutral' : 'bad'}>
      <ReadFailed tone="bad" what={what} error={error} onRetry={onRetry} />
    </Notice>
  );
}

/** The same wait as its own grey box, for a screen that draws its failure by hand: `kept` — rows from an earlier read are
 *  still on the page. */
export function OfflineNotice({ what, kept = false }: { what: string; kept?: boolean }) {
  return <Notice tone="neutral"><OfflineLine what={what} kept={kept} /></Notice>;
}

const TASK_TONE = { neutral: 'bg-panel-2 text-tg-hint', warn: 'bg-warn-bg text-warn', bad: 'bg-neg-bg text-neg' } as const;

/** ONE THING THAT NEEDS HIM: an icon in its tone, what (a few words), who and how much (one muted line), and the one
 *  button that opens it — a bordered secondary, so the screen's gold primary stays its own. */
export function TaskRow({ icon: Icon, tone = 'neutral', title, sub, action, to }: {
  icon: LucideIcon;
  tone?: keyof typeof TASK_TONE;
  title: ReactNode;
  sub?: ReactNode;
  action: string;
  to: string;
}) {
  return (
    <div className="grid grid-cols-[32px_minmax(0,1fr)_auto] items-center gap-3 border-b border-stroke-soft px-4 py-4 last:border-b-0 lg:px-5">
      <span className={`grid h-8 w-8 place-items-center rounded-lg ${TASK_TONE[tone]}`} aria-hidden>
        <Icon className="h-4 w-4" />
      </span>
      <div className="min-w-0">
        <div className="text-body font-medium">{title}</div>
        {sub && <div className="mt-1 truncate text-meta text-tg-hint">{sub}</div>}
      </div>
      <Link to={to} className="btn-secondary px-3 text-body">{action}</Link>
    </div>
  );
}

/** A DOOR TO ANOTHER SCREEN: icon, the screen's routes.ts name, ≤ 4 words of what it is for. */
export function Tile({ to, icon: Icon, title, sub }: { to: string; icon?: LucideIcon; title: string; sub?: string }) {
  return (
    <Link to={to} className="flex min-h-[64px] items-center gap-3 rounded-[10px] border border-stroke bg-panel px-4 py-3 text-tg-text transition-colors hover:border-tg-accent lg:min-h-[76px]">
      {Icon && <Icon className="h-[18px] w-[18px] shrink-0 text-tg-hint" aria-hidden />}
      <span className="min-w-0 flex-1">
        <span className="block text-body font-medium">{title}</span>
        {sub && <span className="mt-1 block truncate text-meta text-tg-hint">{sub}</span>}
      </span>
      <ChevronRight className="h-4 w-4 shrink-0 text-tg-hint" aria-hidden />
    </Link>
  );
}

/** Tiles under a heading: one column on the phone, two from md, three from lg. */
export function TileGroup({ heading, children }: { heading: string; children: ReactNode }) {
  return (
    <section className="mb-6">
      <h2 className="mb-3 text-label font-medium uppercase tracking-wider text-tg-hint">{heading}</h2>
      <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-3 lg:gap-4">{children}</div>
    </section>
  );
}

/** TWO TO FOUR FIGURES THAT ANSWER THE SCREEN'S QUESTION, side by side on the laptop, stacked on the phone. Draw it
 *  only from a successful read: a figure the books did not give is `—`, never 0. */
export function SummaryStrip({ items, phoneCols = 1, cols = 3 }: {
  items: ReadonlyArray<{ label: string; value: ReactNode; sub?: ReactNode }>;
  /** Four across from sm when the strip holds four figures (a settled cheque: deposit, balance, seller, spread). */
  cols?: 3 | 4;
  /** Two cells a row on the phone (Astra's party strip) when the figures are short enough to sit side by side. */
  phoneCols?: 1 | 2;
}) {
  return (
    <div className={`mb-4 grid gap-3 rounded-[10px] border border-stroke bg-panel p-4 ${cols === 4 ? 'sm:grid-cols-4' : 'sm:grid-cols-3'} lg:mb-6 lg:gap-6 lg:px-6 lg:py-5 ${phoneCols === 2 ? 'grid-cols-2' : 'grid-cols-1'}`}>
      {items.map((it, i) => (
        <div key={it.label} className={`min-w-0 ${i > 0 ? 'sm:border-l sm:border-stroke sm:pl-6' : ''}`}>
          <div className="text-label font-medium uppercase tracking-wider text-tg-hint">{it.label}</div>
          <div className="mt-1 font-mono tabular text-figure font-semibold">{it.value}</div>
          {/* A cell's second figure ("1,250,000 USDT") is read at body size, quiet — never at meta (Astra's audit P4, 1 Nov 2026). */}
          {it.sub && <div className="mt-1 text-body text-tg-hint">{it.sub}</div>}
        </div>
      ))}
    </div>
  );
}

/** A FIRST READ ON ITS WAY: grey bars where the rows will be, and the word for it. Never drawn for a failed read
 *  (that is a Notice) and never for an empty one (that is an EmptyState). */
export function SkeletonRows({ rows = 4, what }: { rows?: number; what: string }) {
  return (
    <div aria-busy="true">
      <p className="sr-only">Reading {what}</p>
      {Array.from({ length: rows }, (_, i) => (
        <div key={i} className="space-y-2 border-b border-stroke-soft px-4 py-4 last:border-b-0 lg:px-5">
          <div className="skeleton w-2/3" />
          <div className="skeleton h-2 w-2/5" />
        </div>
      ))}
    </div>
  );
}

// ── REGISTERS (batch 2): a list screen's rows, its search and its detail view ─────────────────────────────────

export interface RegisterColumn<R> {
  key: string;
  /** The column head — a label, ≤ 3 words, the Books' own words. */
  head: string;
  align?: 'left' | 'right';
  /** A width class for the laptop table (`w-[34%]`); the rest share what is left. */
  width?: string;
  cell: (row: R) => ReactNode;
  /** What the column sorts by: a head with this is a button (Register's `sort` / `onSort`). Empty values go last. */
  sortBy?: (row: R) => string | number | null | undefined;
  /** The first press's direction — newest or largest first by default for a right-aligned figure, A→Z otherwise. */
  firstDir?: 'asc' | 'desc';
  /** The When column's own time (`whenColumn`): the cards draw it in their left gutter. */
  whenOf?: (row: R) => string | null | undefined;
  /** A seat drawn only from this width up (1024 has no room for every seat: the When column waits for 1280, and below it the
   *  row's own second line says the time). */
  from?: 'xl';
}

/** THE WHEN COLUMN — first in every register, narrow and mono, KYT's Incoming payments its model (his words, 1 Nov 2026:
 *  "Amount, chain, date and time everything in 1st column- doesn't it look wrong"): "10:31" today, "25 Oct, 22:05" any other
 *  day (fmtStamp, one formatter for every screen). Wherever the table is drawn the time has this seat — never stacked under
 *  the name; where the seats do not fit, the register draws its cards (`tableFromColumns`), whose meta line carries the time.
 *  Sorts newest first on the first press. */
export function whenColumn<R>(at: (row: R) => string | null | undefined, opts: { sortable?: boolean } = {}): RegisterColumn<R> {
  return {
    // 72 px at 1024 (the day over the time), 136 px from 1280 (one line) — so a 1024 laptop keeps its table.
    key: 'when', head: 'When', width: 'w-[4.5rem] xl:w-[8.5rem]', whenOf: at,
    ...(opts.sortable ? { sortBy: (r: R) => { const v = at(r); return v ? Date.parse(v) : null; }, firstDir: 'desc' as const } : {}),
    cell: (r) => {
      const v = at(r);
      if (!v) return <span className="font-mono text-body text-tg-hint">—</span>;
      const stamp = fmtStamp(v);
      const [day, time] = stamp.includes(', ') ? stamp.split(', ') : ['', stamp];
      return (
        <span className="font-mono tabular text-body">
          {day && <span className="block whitespace-nowrap text-meta text-tg-hint xl:inline xl:text-body xl:text-tg-text">{day}<span className="hidden xl:inline">, </span></span>}
          <span className="whitespace-nowrap">{time}</span>
        </span>
      );
    },
  };
}

/** A CARD'S TIME, LEADING IT — never under the name (his "date and time everything in 1st column", 1 Nov 2026). From 640 px a
 *  52 px left gutter, mono, top-aligned: "10:31", or the day over the time ("25 Oct" / "11:30"). On a phone, where a gutter
 *  would cost the figures their width, the card's first line: "25 Oct, 11:30" above the name. */
export function WhenGutter({ at }: { at: string | null | undefined }) {
  const stamp = at ? fmtStamp(at) : '—';
  const [day, time] = stamp.includes(', ') ? stamp.split(', ') : ['', stamp];
  return (
    <span className="mb-1 flex gap-1 font-mono tabular text-meta leading-5 text-tg-hint sm:mb-0 sm:block sm:w-[3.25rem] sm:shrink-0 sm:pt-0.5">
      {day && <span className="whitespace-nowrap sm:block">{day}<span className="sm:hidden">,</span></span>}
      <span className="whitespace-nowrap text-tg-text sm:block">{time}</span>
    </span>
  );
}

/** The time on a row's second line where a register draws no When seat (kept for the party statement's rows, which use it). */
export function WhenInline({ at, sep = true }: { at: string | null | undefined; sep?: boolean }) {
  return at ? <span className="xl:hidden">{sep ? ' · ' : ''}{fmtStamp(at)}</span> : null;
}

/** WHERE A REGISTER'S TABLE STARTS — the laptop widths the console is used at, and the table's own width at each (the page less
 *  the 212 px rail and the column's padding): 1024 → about 748 px, 1280 → about 1004, 1440 → about 1164. */
export type TableFrom = 'lg' | 'xl' | 'wide';
const TABLE_SHOW: Record<TableFrom, string> = { lg: 'lg:table', xl: 'xl:table', wide: 'min-[1440px]:table' };
const CARDS_HIDE: Record<TableFrom, string> = { lg: 'lg:hidden', xl: 'xl:hidden', wide: 'min-[1440px]:hidden' };
const TABLE_PX: Record<TableFrom, number> = { lg: 748, xl: 1004, wide: 1164 };
/** A name seat narrower than this is not readable on two lines (Parties and Inventory at 1024 drew one letter a line). */
const NAME_MIN_PX = 128;
/** A seat's width from its class: `w-[8.5rem]` 136 px, `w-[148px]`, `w-10` 40 px, `w-[26%]` a share; the first width named
 *  (the narrow screen's) when a class names two. */
function seatWidth(width: string | undefined): { px: number; share: number } {
  const m = /(?:^|\s)w-(?:\[([\d.]+)(rem|px|%)\]|(\d+))/.exec(width ?? '');
  if (!m) return { px: 0, share: 0 };
  if (m[3]) return { px: Number(m[3]) * 4, share: 0 };
  const n = Number(m[1]);
  return m[2] === '%' ? { px: 0, share: n / 100 } : { px: m[2] === 'rem' ? n * 16 : n, share: 0 };
}
/** The first width at which the fixed seats, the shares and a readable name all fit; the cards serve every width below it.
 *  (A seat's width holds its own padding.) A register too wide even at 1440 still draws its table there. */
export function tableFromColumns(columns: ReadonlyArray<Pick<RegisterColumn<unknown>, 'width' | 'from'>>): TableFrom {
  for (const at of ['lg', 'xl', 'wide'] as const) {
    const shown = columns.filter((c) => !c.from || at !== 'lg');
    let px = 0, share = 0, open = 0;
    for (const c of shown) {
      const w = seatWidth(c.width);
      if (w.px) px += w.px; else if (w.share) share += w.share; else open += 1;
    }
    const left = TABLE_PX[at] * (1 - share) - px;
    if (left >= NAME_MIN_PX * Math.max(1, open)) return at;
  }
  return 'wide';
}

/** A register's sort: a column's key and its direction. */
export interface RegisterSort { key: string; dir: 'asc' | 'desc' }

/** A run of rows under one head (a Dubai day, a wallet): the head, its own totals, and whether it folds. */
export interface RegisterGroup<R> {
  key: string;
  /** Null: a run with no head of its own (the rows simply continue the list — Chain's rows between two folded days). */
  head: ReactNode;
  /** The group's own figures, beside its head — "4 · $136,614 · 1 alert". */
  totals?: ReactNode;
  /** The group's figures UNDER THE COLUMNS THEY TOTAL on the laptop table (a column's key → its total), so a total is read where
   *  its figures are, never as a sentence beside the head (his "everything in 1st column", 1 Nov 2026). The phone's card list
   *  keeps `totals` beside the head. */
  cells?: Partial<Record<string, ReactNode>>;
  rows: readonly R[];
}

/** ONE ROW SPEC, TWO DRAWINGS: a table from lg (heads, aligned figures, a hairline per row) and a card per row on the
 *  phone (`card` lays out its title, its figure, its meta line and its state). A row opens its detail by `href` (a link:
 *  Back returns to the list) or `onOpen`. The register never claims "none": the screen draws an EmptyState only after
 *  a successful read, and a partial list says so above it. */
export function Register<R>({ label, columns, rows = [], groups, folded, onFold, sort, onSort, stickyHead = false, tableFrom: asked, rowKey, href, onOpen, card }: {
  label: string;
  columns: ReadonlyArray<RegisterColumn<R>>;
  rows?: readonly R[];
  /** Rows in runs under a head each (a day, a wallet) — `rows` is not read when these are given. */
  groups?: ReadonlyArray<RegisterGroup<R>>;
  /** The groups folded shut (keys), and the press that folds or opens one — `useFoldParam` keeps both in the URL. */
  folded?: ReadonlySet<string>;
  onFold?: (key: string) => void;
  /** The sort, and the press on a sortable head — `useSortParam` keeps it in the URL. Sorts within each group. */
  sort?: RegisterSort | null;
  onSort?: (next: RegisterSort) => void;
  /** The column heads stay in view under the top bar while the page scrolls (lg; the phone draws cards, not heads). */
  stickyHead?: boolean;
  /** Where the table starts. Left out, the register works it out from its columns (`tableFromColumns`): the table only from
   *  the width where every fixed seat AND a readable name fit, the cards below it — so no figure is cut and no name is drawn
   *  one letter a line (Parties at 1024, Astra's audit, 1 Nov 2026). */
  tableFrom?: TableFrom;
  rowKey: (row: R) => string;
  href?: (row: R) => string;
  onOpen?: (row: R) => void;
  card: (row: R) => { title: ReactNode; figure?: ReactNode; meta?: ReactNode; state?: ReactNode };
}) {
  const navigate = useNavigate();
  const tableFrom = asked ?? tableFromColumns(columns);
  const whenCol = columns.find((c) => c.whenOf);
  const by = sort ? columns.find((c) => c.key === sort.key && c.sortBy) : undefined;
  const sorted = (list: readonly R[]): readonly R[] => {
    if (!by?.sortBy || !sort) return list;
    const val = by.sortBy, sign = sort.dir === 'asc' ? 1 : -1;
    return list.map((r, i) => ({ r, i, v: val(r) })).sort((a, b) => {
      const ea = a.v === null || a.v === undefined || a.v === '', eb = b.v === null || b.v === undefined || b.v === '';
      if (ea || eb) return ea === eb ? a.i - b.i : ea ? 1 : -1;   // empties last, either way
      const d = typeof a.v === 'number' && typeof b.v === 'number' ? a.v - b.v : String(a.v).localeCompare(String(b.v));
      return d === 0 ? a.i - b.i : d * sign;
    }).map((x) => x.r);
  };
  const runs: ReadonlyArray<RegisterGroup<R>> = groups ?? [{ key: '', head: null, rows }];
  // The row's link sits on its NAME, the first seat that is always drawn (a When seat before it hides below 1280).
  const firstShown = Math.max(0, columns.findIndex((c) => !c.from));
  // A group's head sits in the first seat drawn at this width: the When seat where the table starts at 1280 (it is drawn
  // there), else the name.
  const firstShownAll = tableFrom === 'lg' ? firstShown : 0;
  const shut = (g: RegisterGroup<R>) => !!groups && !!folded?.has(g.key);
  const press = (c: RegisterColumn<R>) => onSort?.(sort?.key === c.key
    ? { key: c.key, dir: sort.dir === 'asc' ? 'desc' : 'asc' }
    : { key: c.key, dir: c.firstDir ?? (c.align === 'right' ? 'desc' : 'asc') });
  const groupHead = (g: RegisterGroup<R>, withTotals = true) => {
    const inner = (
      <>
        <span className="flex min-w-0 items-center gap-2 text-body font-medium text-tg-text">
          {onFold && <ChevronRight className={`h-4 w-4 shrink-0 text-tg-hint transition-transform ${shut(g) ? '' : 'rotate-90'}`} aria-hidden />}
          <span className="shrink-0 whitespace-nowrap">{g.head}</span>
        </span>
        {withTotals && g.totals && <span className="flex min-w-0 flex-wrap justify-end gap-x-4 gap-y-1 text-right font-mono tabular text-meta text-tg-hint">{g.totals}</span>}
      </>
    );
    return onFold
      ? <button type="button" aria-expanded={!shut(g)} onClick={() => onFold(g.key)} className="flex min-h-11 w-full items-center justify-between gap-3 text-left lg:min-h-9">{inner}</button>
      : <div className="flex min-h-11 items-center justify-between gap-3 lg:min-h-9">{inner}</div>;
  };
  const open = (row: R, children: ReactNode, className: string) =>
    href ? <Link to={href(row)} className={className}>{children}</Link>
      : onOpen ? <button type="button" onClick={() => onOpen(row)} className={`${className} w-full text-left`}>{children}</button>
        : <div className={className}>{children}</div>;
  return (
    <div aria-label={label}>
      <table className={`hidden w-full table-fixed border-collapse ${TABLE_SHOW[tableFrom]}`}>
        <thead className={stickyHead ? 'sticky top-16 z-10' : ''}>
          <tr>
            {columns.map((c) => {
              const on = sort?.key === c.key && !!c.sortBy;
              const Arrow = sort?.dir === 'asc' ? ArrowUp : ArrowDown;
              return (
                <th key={c.key} scope="col" aria-sort={on ? (sort!.dir === 'asc' ? 'ascending' : 'descending') : undefined}
                  className={`border-b border-stroke bg-panel-2 px-3 py-3 text-label font-medium uppercase tracking-wider text-tg-hint xl:px-4 ${c.align === 'right' ? 'text-right' : 'text-left'} ${c.width ?? ''} ${c.from === 'xl' ? 'hidden xl:table-cell' : ''}`}>
                  {c.sortBy && onSort ? (
                    <button type="button" onClick={() => press(c)}
                      className={`inline-flex items-center gap-1 uppercase tracking-wider hover:text-tg-text ${on ? 'text-tg-text' : ''} ${c.align === 'right' ? 'flex-row-reverse' : ''}`}>
                      {c.head}
                      {on ? <Arrow className="h-3 w-3" aria-hidden /> : <span className="h-3 w-3" aria-hidden />}
                    </button>
                  ) : c.head}
                </th>
              );
            })}
          </tr>
        </thead>
        {runs.map((g) => (
          <tbody key={g.key}>
            {groups && !g.cells && g.head != null && (
              <tr>
                <th colSpan={columns.length} scope="rowgroup" className="border-b border-stroke-soft bg-panel-2/50 px-4 py-1 text-left font-normal normal-case tracking-normal">{groupHead(g)}</th>
              </tr>
            )}
            {groups && g.cells && (() => {
              // The head spans the seats before the first total (the When seat alone is too narrow for "USDT·TRC").
              const firstTotal = columns.findIndex((c, i) => i > firstShownAll && g.cells?.[c.key] != null);
              const span = firstTotal < 0 ? columns.length - firstShownAll : firstTotal - firstShownAll;
              return (
                <tr className="border-b border-stroke-soft bg-panel-2/50">
                  {columns.map((c, i) => (i > firstShownAll && i < firstShownAll + span) || i < firstShownAll ? null : (
                    <th key={c.key} scope={i === firstShownAll ? 'rowgroup' : undefined} colSpan={i === firstShownAll ? span : undefined}
                      className={`px-3 py-1 align-middle font-normal normal-case tracking-normal text-body xl:px-4 ${i === firstShownAll ? 'text-left' : c.align === 'right' ? 'text-right' : 'text-left'} ${c.from === 'xl' ? 'hidden xl:table-cell' : ''}`}>
                      {i === firstShownAll ? groupHead(g, false) : g.cells?.[c.key] ?? null}
                    </th>
                  ))}
                </tr>
              );
            })()}
            {!shut(g) && sorted(g.rows).map((r) => (
              <tr key={rowKey(r)} className={`border-b border-stroke-soft last:border-b-0 ${href || onOpen ? 'cursor-pointer hover:bg-panel-2' : ''}`}
                onClick={href || onOpen ? (e) => { if ((e.target as HTMLElement).closest('a,button')) return; if (onOpen) onOpen(r); else if (href) navigate(href(r)); } : undefined}>
                {columns.map((c, i) => (
                  <td key={c.key} className={`px-3 py-3 align-top text-body xl:px-4 ${c.align === 'right' ? 'text-right' : ''} ${c.from === 'xl' ? 'hidden xl:table-cell' : ''}`}>
                    {i === firstShown && (href || onOpen) ? open(r, c.cell(r), 'block min-w-0 hover:underline') : c.cell(r)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        ))}
      </table>
      <ul className={CARDS_HIDE[tableFrom]}>
        {runs.map((g) => [
          groups && g.head != null && <li key={`head:${g.key}`} className="border-b border-stroke-soft bg-panel-2/50 px-4">{groupHead(g)}</li>,
          ...(shut(g) ? [] : sorted(g.rows)).map((r) => {
          const k = card(r);
          const body = (
            <>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 break-words text-body font-medium">{k.title}</div>
                {k.figure && <div className="shrink-0 text-right text-body">{k.figure}</div>}
              </div>
              {(k.meta || k.state) && (
                <div className="mt-2 flex items-center justify-between gap-3">
                  <div className="min-w-0 truncate text-meta text-tg-hint">{k.meta}</div>
                  {k.state && <div className="shrink-0">{k.state}</div>}
                </div>
              )}
            </>
          );
          return (
            <li key={rowKey(r)} className="border-b border-stroke-soft last:border-b-0">
              {open(r, whenCol ? (
                // THE CARD'S TIME IN ITS OWN LEFT GUTTER (his "date and time everything in 1st column", 1 Nov 2026): narrow, mono,
                // top-aligned — "10:31", or the day over the time — and the card's name and figures beside it, never under the time.
                <div className="sm:flex sm:items-start sm:gap-3">
                  <WhenGutter at={whenCol.whenOf?.(r)} />
                  <div className="min-w-0 flex-1">{body}</div>
                </div>
              ) : body, 'block px-4 py-4 active:bg-panel-2')}
            </li>
          );
          })])}
      </ul>
    </div>
  );
}

/** Which of a register's groups are folded shut, kept in the URL (`?fold=2026-10-26,2026-10-25`): Back and a shared
 *  link keep the view. Returns the folded keys and the press that folds or opens one. */
export function useFoldParam(param = 'fold'): [ReadonlySet<string>, (key: string) => void] {
  const [search, setSearch] = useSearchParams();
  const raw = search.get(param) ?? '';
  const folded = useMemo(() => new Set(raw.split(',').filter(Boolean)), [raw]);
  const toggle = (key: string) => {
    const next = new Set(folded);
    if (next.has(key)) next.delete(key); else next.add(key);
    const s = new URLSearchParams(search);
    if (next.size) s.set(param, [...next].join(',')); else s.delete(param);
    setSearch(s, { replace: true });
  };
  return [folded, toggle];
}

/** A register's sort, kept in the URL (`?sort=amount:desc`); a bare key reads as its fallback's direction. */
export function useSortParam(param = 'sort', fallback: RegisterSort | null = null): [RegisterSort | null, (next: RegisterSort) => void] {
  const [search, setSearch] = useSearchParams();
  const raw = search.get(param);
  const sort = useMemo<RegisterSort | null>(() => {
    if (!raw) return fallback;
    const [key, dir] = raw.split(':');
    return key ? { key, dir: dir === 'asc' || dir === 'desc' ? dir : fallback?.dir ?? 'desc' } : fallback;
  }, [raw, fallback?.key, fallback?.dir]);   // eslint-disable-line react-hooks/exhaustive-deps
  const set = (next: RegisterSort) => {
    const s = new URLSearchParams(search);
    s.set(param, `${next.key}:${next.dir}`);
    setSearch(s, { replace: true });
  };
  return [sort, set];
}

/** A FILTER THAT IS ON, said where the list starts: its words, lit, and a ✕ that takes it off ("Office TRC20 hot
 *  wallet ✕"). The screen keeps the filter in its URL and clears that param in `onClear`. */
export function FilterChip({ label, onClear, clearLabel }: { label: ReactNode; onClear: () => void; clearLabel?: string }) {
  return (
    <span className="chip chip-on max-w-full gap-1 rounded-full border pr-1 text-meta">
      <span className="min-w-0 truncate">{label}</span>
      <button type="button" onClick={onClear} aria-label={clearLabel ?? (typeof label === 'string' ? `Clear ${label}` : 'Clear this filter')}
        className="tap-44 grid h-6 w-6 shrink-0 place-items-center rounded-full hover:bg-tg-accent/15">
        <X className="h-3.5 w-3.5" aria-hidden />
      </button>
    </span>
  );
}

/** SEARCH FIRST, FILTERS BEHIND ONE BUTTON: the box (a reference, a party, an amount — whatever the screen's `q`
 *  reads), and "Filters" with the count of filters set; the panel opens beneath. `extra` sits at the right (a date
 *  control, a count). */
export function SearchBar({ value, onChange, placeholder, filters, active = 0, extra }: {
  value: string;
  onChange: (v: string) => void;
  placeholder: string;
  filters?: ReactNode;
  active?: number;
  extra?: ReactNode;
}) {
  const [open, setOpen] = useState(active > 0);
  return (
    <div className="mb-4 space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <label className="relative min-w-[180px] flex-1">
          <span className="sr-only">{placeholder}</span>
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-tg-hint" aria-hidden />
          <input type="search" value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} className="input !pl-9" />
        </label>
        {filters && (
          <button type="button" aria-expanded={open} onClick={() => setOpen((v) => !v)} className="btn-secondary px-3 text-body">
            <SlidersHorizontal className="h-4 w-4" aria-hidden />Filters{active > 0 && <span className="font-mono tabular text-tg-accent">{active}</span>}
          </button>
        )}
        {extra}
      </div>
      {filters && open && <div className="rounded-[10px] border border-stroke bg-panel p-4">{filters}</div>}
    </div>
  );
}

/** A FOCUSED DETAIL VIEW over its list: a panel at the right from lg (the list stays readable beside it), the whole
 *  screen on the phone. Escape and the close button leave it; the list underneath never moves.
 *
 *  A DIALOG A KEYBOARD AND A SCREEN READER CAN USE (27 Oct 2026, Screens' review): named by its title; on opening it
 *  takes the focus itself (the panel, not a field — a field would pop the phone's keyboard); Tab and Shift+Tab go round
 *  inside it and never into the page beneath; on closing, the focus goes back to what opened it. */
const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]):not([type=hidden]), select:not([disabled]), textarea:not([disabled]), summary, [tabindex]:not([tabindex="-1"])';
export function DetailSheet({ title, onClose, children, footer, wide = false }: {
  title: ReactNode;
  onClose: () => void;
  children: ReactNode;
  footer?: ReactNode;
  /** 720 px from lg instead of 480: room for two records side by side (a transfer against its book entry). */
  wide?: boolean;
}) {
  useEffect(() => {
    const k = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', k);
    return () => window.removeEventListener('keydown', k);
  }, [onClose]);
  const titleId = useId();
  const panel = useRef<HTMLElement>(null);
  // Once per opening — not per render, or a sheet whose onClose is a fresh function would pull the focus back each time.
  useEffect(() => {
    const opener = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    panel.current?.focus({ preventScroll: true });
    return () => { if (opener?.isConnected) opener.focus({ preventScroll: true }); };
  }, []);
  const keepFocus = (e: ReactKeyboardEvent<HTMLElement>) => {
    if (e.key !== 'Tab' || !panel.current) return;
    const stops = [...panel.current.querySelectorAll<HTMLElement>(FOCUSABLE)].filter((el) => el.getClientRects().length > 0);
    const first = stops[0], last = stops[stops.length - 1], at = document.activeElement;
    if (!first || !last) { e.preventDefault(); return; }
    if (e.shiftKey && (at === first || at === panel.current)) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && at === last) { e.preventDefault(); first.focus(); }
  };
  return (
    <>
      <div className="fixed inset-0 z-40 bg-black/40 max-lg:hidden" onClick={onClose} aria-hidden />
      <aside ref={panel} role="dialog" aria-modal="true" aria-labelledby={titleId} tabIndex={-1} onKeyDown={keepFocus}
        className={`fixed inset-0 z-50 flex flex-col bg-panel outline-none lg:inset-y-0 lg:left-auto lg:right-0 lg:border-l lg:border-stroke ${wide ? 'lg:w-[720px]' : 'lg:w-[480px]'}`}>
        <div className="flex min-h-[56px] items-center justify-between gap-3 border-b border-stroke px-4 lg:px-5">
          {/* The name rides on a span inside the head, so the h2 stays the plain head every sheet's tests read. */}
          <h2 className="min-w-0 text-title font-semibold"><span id={titleId}>{title}</span></h2>
          <button type="button" onClick={onClose} className="grid h-11 w-11 place-items-center rounded-lg text-tg-hint hover:bg-panel-2" aria-label="Close">
            <X className="h-5 w-5" aria-hidden />
          </button>
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain p-4 lg:p-5">{children}</div>
        {footer && <div className="border-t border-stroke p-4 pb-[max(16px,env(safe-area-inset-bottom))] lg:px-5">{footer}</div>}
      </aside>
    </>
  );
}

/** A label and its value on one line, in a detail view (Astra's readonly field). */
export function Fact({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="grid grid-cols-[112px_minmax(0,1fr)] gap-4 border-b border-stroke-soft py-3 last:border-b-0">
      <span className="text-meta text-tg-hint">{label}</span>
      <span className="min-w-0 break-words text-right text-body">{children}</span>
    </div>
  );
}
